/**
 * Phase 105-Agent-C — Offline location queue.
 *
 * Lightweight hand-rolled IndexedDB wrapper. Only one collection
 * ("pings") with auto-incrementing primary keys. When `useAgentLocation`
 * can't reach `POST /api/agent/location` (network error / offline),
 * the latest position is pushed to this queue (cap 10, oldest first
 * is evicted). On reconnect the hook drains the queue oldest-first.
 *
 * IndexedDB is preferred over localStorage because:
 *  • Atomic add/delete on individual rows.
 *  • Survives across tab restarts without us hand-rolling JSON parse
 *    guards.
 *
 * We deliberately avoid `idb-keyval` so no new dependency is added.
 */
const DB_NAME = "pickngo-agent-loc";
const STORE = "pings";
const CAP = 10;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexeddb-unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueLocationPing(payload) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      // Evict oldest if at cap.
      const countReq = store.count();
      countReq.onsuccess = () => {
        if (countReq.result >= CAP) {
          const cursorReq = store.openCursor();
          cursorReq.onsuccess = () => {
            const cur = cursorReq.result;
            if (cur) {
              cur.delete();
              const addReq = store.add({ ...payload, queued_at: Date.now() });
              addReq.onsuccess = () => resolve(addReq.result);
              addReq.onerror = () => reject(addReq.error);
            }
          };
          cursorReq.onerror = () => reject(cursorReq.error);
        } else {
          const addReq = store.add({ ...payload, queued_at: Date.now() });
          addReq.onsuccess = () => resolve(addReq.result);
          addReq.onerror = () => reject(addReq.error);
        }
      };
      countReq.onerror = () => reject(countReq.error);
    });
  } catch {
    return null;
  }
}

export async function drainLocationQueue(sendOne) {
  try {
    const db = await openDb();
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    rows.sort((a, b) => (a.queued_at || 0) - (b.queued_at || 0));
    for (const row of rows) {
      try {
        const ok = await sendOne(row);
        if (!ok) break;
        const db2 = await openDb();
        await new Promise((resolve) => {
          const tx = db2.transaction(STORE, "readwrite");
          tx.objectStore(STORE).delete(row.id);
          tx.oncomplete = resolve;
          tx.onerror = resolve;
        });
      } catch {
        break;
      }
    }
  } catch {
    /* indexeddb unavailable — caller swallows. */
  }
}
